import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_TINYCMS_SITE, UPDATE_TINYCMS_SITE } from '../../graphql';

/**
 * The Ask page's own photo and words (site document askPage, apiserver
 * siteDocument.js): the page opens on the photo, full width, with the headline
 * and intro over it, and the three are what a shared link to /ask shows.
 * Saved onto the site's working copy like the logo and cover; readers see them
 * once the site is published (Manage → Design). Agents set the same fields
 * with update_site askPage.
 */

const LIMITS = { headline: 120, intro: 300 };

interface AskForm {
  headline: string;
  intro: string;
  imageUrl: string;
  imageAlt: string;
}

export function AskPageSettings({ accountId, canEdit }: { accountId: string; canEdit: boolean }) {
  const { data, refetch } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [updateSite, { loading: saving }] = useMutation(UPDATE_TINYCMS_SITE);
  const site = data?.tinycmsSite;
  const saved = site?.working?.askPage || {};
  const image = saved.image || null;
  const loaded: AskForm = {
    headline: saved.headline || '',
    intro: saved.intro || '',
    imageUrl: image?.url || '',
    imageAlt: image?.alt || '',
  };
  const [form, setForm] = useState<AskForm>(loaded);
  const [error, setError] = useState<string | null>(null);
  useEffect(
    () => setForm(loaded),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loaded.headline, loaded.intro, loaded.imageUrl, loaded.imageAlt]
  );

  /** Only what changed, onto the working copy. */
  const save = async () => {
    const patch: Record<string, unknown> = {};
    const headline = form.headline.trim();
    const intro = form.intro.trim();
    if (headline !== loaded.headline) patch.headline = headline || null;
    if (intro !== loaded.intro) patch.intro = intro || null;
    const url = form.imageUrl.trim();
    if (url !== loaded.imageUrl || form.imageAlt !== loaded.imageAlt) {
      // Same photo, new alt text: keep it as the media library item it is.
      const same = url === loaded.imageUrl && image?.assetId;
      patch.image = url
        ? { ...(same ? { assetId: image.assetId } : { url }), alt: form.imageAlt }
        : null;
    }
    if (!Object.keys(patch).length) return;
    setError(null);
    try {
      await updateSite({ variables: { accountId, input: { askPage: patch } } });
      await refetch();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const disabled = !canEdit || saving;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ font: '500 13px var(--ink-font)' }}>Ask page photo and words</span>
      <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
        The page opens on your photo, full width, with your headline and intro over it. They’re also
        what a shared link to the Ask page shows. Left empty, the page uses its own words and your
        site’s share image.
      </span>
      <div className="ink-site-fields" style={{ marginTop: 4 }}>
        <div className="ink-site-label">
          Photo
          <div className="ink-site-media">
            {form.imageUrl ? (
              <img className="ink-site-cover" src={form.imageUrl} alt="" />
            ) : (
              <span className="ink-site-cover" />
            )}
            <label className="ink-site-alt">
              Alt text · required with a photo
              <input
                className="ink-site-input"
                value={form.imageAlt}
                placeholder="Alt text"
                disabled={disabled}
                onChange={(e) => setForm({ ...form, imageAlt: e.target.value })}
                onBlur={() => void save()}
              />
            </label>
          </div>
          <input
            className="ink-site-input"
            value={form.imageUrl}
            placeholder="https://example.com/photo.jpg"
            aria-label="Ask page photo address"
            disabled={disabled}
            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
            onBlur={() => void save()}
          />
        </div>
        <label className="ink-site-label">
          Headline
          <input
            className="ink-site-input"
            value={form.headline}
            maxLength={LIMITS.headline}
            placeholder="Have a question? Ask us."
            disabled={disabled}
            onChange={(e) => setForm({ ...form, headline: e.target.value })}
            onBlur={() => void save()}
          />
        </label>
        <label className="ink-site-label">
          Intro
          <textarea
            className="ink-site-input"
            rows={3}
            value={form.intro}
            maxLength={LIMITS.intro}
            placeholder="One or two lines under the headline"
            disabled={disabled}
            onChange={(e) => setForm({ ...form, intro: e.target.value })}
            onBlur={() => void save()}
          />
        </label>
      </div>
      {error ? (
        <span className="ink-site-row-desc" style={{ marginTop: 0, color: 'var(--ink-red)' }}>
          {error}
        </span>
      ) : null}
      <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
        Saved with your site’s other changes. Nothing goes live until you publish the site in{' '}
        <Link to="/content?view=manage&area=design">Design</Link>.
      </span>
    </div>
  );
}
